import { getFirestore, doc, getDoc, collection, query, orderBy, getDocs, addDoc, deleteDoc, serverTimestamp, runTransaction, increment, setDoc, getCountFromServer, updateDoc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase-config.js";
import { InAppNotificationService } from "./in-app-notification.service.js";

const db = getFirestore(app);

export const UserService = {
    /**
     * Get User Public Profile
     */
    getUserProfile: async (userId) => {
        try {
            // [SECURE] Ironclad Proxy Pattern
            // 1. Check if Self (Owner) -> Direct DB Access (Fast, Allowed)
            // 2. If Other -> Cloud Function Proxy (Secure, Sanitized)

            // Dynamic Import for Auth/Functions
            const { getAuth } = await import("firebase/auth");
            const { getFunctions, httpsCallable } = await import("firebase/functions");

            const auth = getAuth(app);
            const currentUser = auth.currentUser;

            if (currentUser && currentUser.uid === userId) {
                // [DIRECT] Owner Access
                const docRef = doc(db, "users", userId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    return { uid: docSnap.id, ...docSnap.data() };
                }
                return null;
            } else {
                // [OPTIMIZATION] Try Public Profile First (Speed: 0.1s)
                try {
                    const publicRef = doc(db, `users/${userId}/public/info`);
                    const publicSnap = await getDoc(publicRef);

                    if (publicSnap.exists()) {
                        // Success! Return fast data
                        return { uid: userId, ...publicSnap.data() };
                    }
                } catch (ignore) {
                    // Fallback to Cloud Function if permission denied or any error
                    console.warn("Public Profile Optimization Skipped:", ignore);
                }

                // [FALLBACK] Cloud Function Proxy (Speed: 2-3s)
                // This bypasses the 'allow read: if false' rule by using Admin SDK on server.
                const functions = getFunctions(app);
                // Note: If you use a specific region, add it like getFunctions(app, 'asia-northeast3');
                // Defaulting to us-central1 as per standard index.js

                const getPublicProfile = httpsCallable(functions, 'getPublicProfile');
                try {
                    const result = await getPublicProfile({ targetUid: userId });
                    return result.data;
                } catch (fnError) {
                    console.warn("Public Profile Fetch Failed", fnError);
                    return null; // Handle User Not Found gracefully
                }
            }
        } catch (error) {
            console.error("UserService.getUserProfile Error:", error);
            throw error;
        }
    },

    /**
     * Follow User (Transactional & Idempotent)
     */
    // [REMOVED DUPLICATE followupUser method to fix build warning]
    // The secure toggleFollow is now the source of truth below.

    /**
     * Unfollow User (Transactional & Idempotent)
     */
    /**
     * Toggle Follow User (Secure Cloud Function)
     * Handles both Follow and Unfollow securely.
     */
    toggleFollow: async (targetUserUid) => {
        try {
            const functions = getFunctions(app, 'us-central1');
            const toggleFollowFn = httpsCallable(functions, 'toggleFollow');

            const result = await toggleFollowFn({ targetUid: targetUserUid });
            return result.data;
        } catch (error) {
            console.error("UserService.toggleFollow Error:", error);
            throw error;
        }
    },

    // Legacy wrappers for compatibility (mapped to toggleFollow)
    followUser: async (currentUser, targetUser) => {
        return UserService.toggleFollow(targetUser.uid);
    },

    unfollowUser: async (currentUserUid, targetUserUid) => {
        return UserService.toggleFollow(targetUserUid);
    },

    /**
     * Check Follow Status
     */
    checkFollowStatus: async (currentUserUid, targetUserUid) => {
        try {
            const docRef = doc(db, `users/${currentUserUid}/following`, targetUserUid);
            const docSnap = await getDoc(docRef);
            return docSnap.exists();
        } catch (error) {
            console.error("UserService.checkFollowStatus Error:", error);
            return false;
        }
    },

    /**
     * Sync Social Counts (Deprecated & Disabled)
     * Reason: Client-side writes to 'users/{id}' are blocked by security rules.
     * Use Cloud Functions for all count updates.
     */
    syncSocialCounts: async (userId) => {
        console.warn("[UserService] syncSocialCounts is deprecated and disabled for security.");
        return null;
    },

    /**
     * Get Followers List
     */
    getFollowers: async (userId) => {
        try {
            const q = query(collection(db, `users/${userId}/followers`), orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => doc.data());
        } catch (error) {
            console.error("UserService.getFollowers Error:", error);
            return [];
        }
    },

    /**
     * Get Following List
     */
    getFollowing: async (userId) => {
        try {
            const q = query(collection(db, `users/${userId}/following`), orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => doc.data());
        } catch (error) {
            console.error("UserService.getFollowing Error:", error);
            return [];
        }
    },

    // [NEW] Real-time Guestbook Subscription
    subscribeGuestbook: (userId, callback) => {
        const q = query(collection(db, `users/${userId}/guestbook`), orderBy('createdAt', 'desc'));
        return onSnapshot(q, (snapshot) => {
            const entries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            callback(entries);
        }, (error) => {
            console.error("subscribeGuestbook Error:", error);
            callback([], error);
        });
    },

    /**
     * Add Guestbook entry with Notification
     */
    addGuestbookEntry: async (targetUserId, entryData) => {
        try {
            const docRef = await addDoc(collection(db, `users/${targetUserId}/guestbook`), {
                ...entryData,
                replyCount: 0,
                createdAt: serverTimestamp()
            });

            // Notification Trigger
            if (targetUserId !== entryData.fromUid) {
                // Determine Type: Image or Text
                let snippet = entryData.content;
                if (!snippet && entryData.imageUrl) snippet = "사진을 남겼습니다.";
                if (snippet && snippet.length > 20) snippet = snippet.substring(0, 20) + "...";

                InAppNotificationService.createNotification(
                    targetUserId,
                    'guestbook',
                    `${entryData.fromName}님이 방명록을 남겼습니다: "${snippet}"`,
                    { uid: entryData.fromUid, displayName: entryData.fromName, photoURL: entryData.fromPhoto },
                    `/user/${targetUserId}?tab=guestbook` // Link to my profile guestbook tab ideally
                );
            }

            return docRef.id;
        } catch (error) {
            console.error("UserService.addGuestbookEntry Error:", error);
            throw error;
        }
    },

    /**
     * Delete Guestbook entry
     */
    deleteGuestbookEntry: async (targetUserId, entryId) => {
        try {
            await deleteDoc(doc(db, `users/${targetUserId}/guestbook`, entryId));
        } catch (error) {
            console.error("UserService.deleteGuestbookEntry Error:", error);
            throw error;
        }
    },

    /**
     * Update Guestbook entry
     */
    updateGuestbookEntry: async (targetUserId, entryId, content) => {
        try {
            const ref = doc(db, `users/${targetUserId}/guestbook`, entryId);
            await updateDoc(ref, {
                content,
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            console.error("UserService.updateGuestbookEntry Error:", error);
            throw error;
        }
    },

    // [NEW] Reply Support
    addGuestbookReply: async (targetUserId, entryId, replyData) => {
        try {
            // Add to subcollection
            await addDoc(collection(db, `users/${targetUserId}/guestbook/${entryId}/replies`), {
                ...replyData,
                createdAt: serverTimestamp()
            });

            // Increment Reply Count on Parent
            await updateDoc(doc(db, `users/${targetUserId}/guestbook`, entryId), {
                replyCount: increment(1)
            });

            // Notification Logic
            // 1. Notify the Guestbook Writer (if not self)
            // Need to fetch original entry to know who wrote it
            const entryRef = doc(db, `users/${targetUserId}/guestbook`, entryId);
            const entrySnap = await getDoc(entryRef);
            if (entrySnap.exists()) {
                const entry = entrySnap.data();
                if (entry.fromUid !== replyData.fromUid) {
                    InAppNotificationService.createNotification(
                        entry.fromUid,
                        'guestbook_reply',
                        `${replyData.fromName}님이 내 방명록 글에 답글을 남겼습니다.`,
                        { uid: replyData.fromUid, displayName: replyData.fromName, photoURL: replyData.fromPhoto },
                        `/user/${targetUserId}?tab=guestbook`
                    );
                }

                // 2. Notify Profile Owner (if Owner isn't the one replying, and Owner isn't the Writer)
                if (targetUserId !== replyData.fromUid && targetUserId !== entry.fromUid) {
                    InAppNotificationService.createNotification(
                        targetUserId,
                        'guestbook_reply',
                        `${replyData.fromName}님이 내 프로필 방명록에 답글을 남겼습니다.`,
                        { uid: replyData.fromUid, displayName: replyData.fromName, photoURL: replyData.fromPhoto },
                        `/user/${targetUserId}?tab=guestbook`
                    );
                }
            }

        } catch (error) {
            console.error("UserService.addGuestbookReply Error:", error);
            throw error;
        }
    },

    subscribeGuestbookReplies: (targetUserId, entryId, callback) => {
        const q = query(collection(db, `users/${targetUserId}/guestbook/${entryId}/replies`), orderBy('createdAt', 'asc'));
        return onSnapshot(q, (snapshot) => {
            const replies = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            callback(replies);
        }, (error) => {
            console.error("subscribeGuestbookReplies Error:", error);
            callback([]);
        });
    },

    deleteGuestbookReply: async (targetUserId, entryId, replyId) => {
        try {
            await deleteDoc(doc(db, `users/${targetUserId}/guestbook/${entryId}/replies`, replyId));
            await updateDoc(doc(db, `users/${targetUserId}/guestbook`, entryId), {
                replyCount: increment(-1)
            });
        } catch (error) {
            console.error("UserService.deleteGuestbookReply Error:", error);
            throw error;
        }
    },

    /**
     * [Phase 3] Real-time Wallet Listener
     * Listens to the user's document for instant balance updates (Stars/Points).
     * Also fetches 'subscription' status for future features.
     * 
     * @param {string} userId - Target User UID
     * @param {function} callback - Function to receive data: { stars, points, subscription }
     * @returns {function} Unsubscribe function
     */
    listenToMyWallet: (userId, callback) => {
        const docRef = doc(db, "users", userId);
        return onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                callback({
                    stars: data.starPoints || 0,
                    points: data.activityPoints || 0,
                    // [Investor Readiness] Hybrid Model Support
                    subscription: data.subscription || { tier: 'free', status: 'none' }
                });
            }
        }, (error) => {
            console.error("UserService.listenToMyWallet Error:", error);
        });
    }
};
